(() => {
  'use strict';

  if (customElements.get('z2-size-guide-modal')) return;

  let activeGuide = null;

  const openerSelector = 'button[data-z2-open-size-guide]';

  const focusableSelector =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';

  const visible = element =>
    Boolean(element.getClientRects().length) &&
    getComputedStyle(element).visibility !== 'hidden';

  const normalize = value =>
    (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ')
      .trim();

  class Z2SizeGuideModal extends HTMLElement {
    disconnectedCallback() {
      this.close();
      this.dialog?.remove();
      this.dialog = null;
    }

    ensureDialog() {
      if (this.dialog) return true;

      const source =
        this.querySelector('template')?.content.firstElementChild;

      if (!source) return false;

      const dialog = source.cloneNode(true);
      const sectionId = this.dataset.sectionId;

      dialog.id = 'z2-size-guide-dialog-' + sectionId;
      dialog.setAttribute('data-z2-size-guide-dialog', '');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute(
        'aria-label',
        this.dataset.label || 'Maattabel'
      );

      dialog.tabIndex = -1;
      dialog.hidden = true;
      dialog.style.display = 'none';

      const closeButton = dialog.querySelector(
        '.size-modal-close-' + sectionId
      );

      if (closeButton) {
        closeButton.type = 'button';
        closeButton.setAttribute('data-z2-size-guide-close', '');
        closeButton.setAttribute('aria-label', 'Sluiten');
      }

      dialog
        .querySelectorAll('.size-modal-video-play-' + sectionId)
        .forEach(button => {
          button.setAttribute('data-z2-size-guide-play', '');
        });

      this.dialog = dialog;

      dialog.addEventListener('click', event => {
        if (
          event.target === dialog ||
          event.target.closest('[data-z2-size-guide-close]')
        ) {
          this.close();
          return;
        }

        const tab = event.target.closest('[data-ss-tab]');

        if (tab) {
          this.setTab(tab.dataset.ssTab);
        }

        const playButton = event.target.closest(
          '[data-z2-size-guide-play]'
        );

        const item = playButton?.closest('[data-ss-image]');
        const video = item?.querySelector('video');

        if (video) {
          video.controls = true;

          const result = video.play();

          result?.catch(() => this.resetVideo(video));
        }
      });

      dialog.addEventListener(
        'play',
        event => {
          const video = event.target;

          if (!(video instanceof HTMLVideoElement)) return;

          const item = video.closest('[data-ss-image]');

          if (
            dialog.hidden ||
            !item?.classList.contains('is-active')
          ) {
            this.resetVideo(video);
          } else {
            item.classList.add('is-playing');
          }
        },
        true
      );

      dialog.addEventListener(
        'ended',
        event => {
          if (event.target instanceof HTMLVideoElement) {
            this.resetVideo(event.target);
          }
        },
        true
      );

      document.body.append(dialog);

      const firstTab = dialog.querySelector('[data-ss-tab]');

      if (firstTab) {
        this.setTab(firstTab.dataset.ssTab);
      }

      return true;
    }

    resetVideo(video) {
      video.pause();

      try {
        video.currentTime = 0;
      } catch (_) {
        // De videometadata kan nog aan het laden zijn.
      }

      video.controls = false;

      video
        .closest('[data-ss-image]')
        ?.classList.remove('is-playing');
    }

    setTab(index) {
      const selected = String(index);

      const groups = [
        ['ssTab', '[data-ss-tab]'],
        ['ssPanel', '[data-ss-panel]'],
        ['ssImage', '[data-ss-image]'],
        ['ssModel', '[data-ss-model]']
      ];

      for (const [key, selector] of groups) {
        this.dialog.querySelectorAll(selector).forEach(element => {
          const isActive = element.dataset[key] === selected;

          element.classList.toggle('is-active', isActive);

          if (key === 'ssTab') {
            element.setAttribute(
              'aria-pressed',
              String(isActive)
            );
          }

          if (key === 'ssImage' && !isActive) {
            element.querySelectorAll('video').forEach(video => {
              this.resetVideo(video);
            });
          }
        });
      }
    }

    open(button) {
      if (!this.ensureDialog()) return;
      if (activeGuide === this) return;

      activeGuide?.close(false);

      this.opener = button;

      this.previousOverflow =
        document.body.style.getPropertyValue('overflow');

      this.previousOverflowPriority =
        document.body.style.getPropertyPriority('overflow');

      document.body.style.setProperty('overflow', 'hidden');

      button.setAttribute('aria-controls', this.dialog.id);

      this.dialog.hidden = false;
      this.dialog.style.removeProperty('display');
      this.dialog.classList.add('active');

      activeGuide = this;

      this.focusStart();
    }

    focusStart() {
      const target =
        this.dialog.querySelector('[data-z2-size-guide-close]') ||
        this.dialog;

      target.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.dialog || activeGuide !== this) return;

      this.dialog.querySelectorAll('video').forEach(video => {
        this.resetVideo(video);
      });

      this.dialog.classList.remove('active');
      this.dialog.hidden = true;
      this.dialog.style.display = 'none';

      if (this.previousOverflow) {
        document.body.style.setProperty(
          'overflow',
          this.previousOverflow,
          this.previousOverflowPriority
        );
      } else {
        document.body.style.removeProperty('overflow');
      }

      activeGuide = null;

      if (restoreFocus && this.opener?.isConnected) {
        this.opener.focus({ preventScroll: true });
      }

      this.opener = null;
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = [
        ...this.dialog.querySelectorAll(focusableSelector)
      ].filter(visible);

      const first = items[0];
      const last = items[items.length - 1];
      const focused = document.activeElement;

      if (!first) {
        event.preventDefault();
        this.dialog.focus();
      } else if (
        event.shiftKey &&
        (focused === first || !items.includes(focused))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (focused === last || !items.includes(focused))
      ) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class Z2SizeGuideInline extends HTMLElement {
    connectedCallback() {
      this.scope =
        document.getElementById(this.dataset.scopeId) ||
        this.closest('[data-section-type="product"]') ||
        this.closest('.shopify-section');

      if (!this.scope) return;

      this.schedule = () => {
        if (this.frame) return;

        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.placeButton();
        });
      };

      this.observer = new MutationObserver(this.schedule);

      this.observe();

      window.addEventListener('resize', this.schedule);
      window.addEventListener(
        'Globo.swatch.loaded',
        this.schedule
      );

      this.schedule();
    }

    observe() {
      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          'data-name',
          'class',
          'style',
          'hidden'
        ]
      });
    }

    placeButton() {
      if (!this.isConnected) return;

      // Eigen wijzigingen mogen geen observer-lus veroorzaken.
      this.observer.disconnect();

      try {
        if (!this.button) {
          const source =
            this.querySelector('template')?.content.firstElementChild;

          if (!source) return;

          this.button = source.cloneNode(true);
        }

        const explicitLabel = normalize(
          this.dataset.optionLabel
        );

        const legends = [
          ...this.scope.querySelectorAll(
            '.globo-swatch-product-detail .swatch--gl legend'
          )
        ];

        const candidates = legends.filter(legend => {
          const label = normalize(
            legend.getAttribute('data-name') ||
            legend.textContent
          );

          if (explicitLabel) {
            return (
              label === explicitLabel ||
              label.startsWith(explicitLabel + ':') ||
              label.startsWith(explicitLabel + ' ')
            );
          }

          return /^(maat|size|grosse|groesse)(?:\s|:|$)/.test(
            label
          );
        });

        // Plaats ook als een bovenliggende pagina-animatie
        // de productsectie nog tijdelijk verborgen houdt.
        const target =
          candidates.find(visible) || candidates[0];

        if (this.legend !== target) {
          this.legend?.removeAttribute(
            'data-z2-size-guide-legend'
          );

          this.button.remove();
          this.legend = target;
        }

        if (target) {
          target.setAttribute(
            'data-z2-size-guide-legend',
            ''
          );

          if (this.button.parentElement !== target) {
            target.append(this.button);
          }
        }
      } finally {
        if (this.isConnected) {
          this.observe();
        }
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();

      cancelAnimationFrame(this.frame);
      this.frame = 0;

      if (this.schedule) {
        window.removeEventListener('resize', this.schedule);
        window.removeEventListener(
          'Globo.swatch.loaded',
          this.schedule
        );
      }

      this.button?.remove();

      this.legend?.removeAttribute(
        'data-z2-size-guide-legend'
      );

      this.legend = null;
    }
  }

  customElements.define(
    'z2-size-guide-modal',
    Z2SizeGuideModal
  );

  customElements.define(
    'z2-size-guide-inline',
    Z2SizeGuideInline
  );

  document.addEventListener('click', event => {
    const button = event.target.closest?.(openerSelector);

    if (!button) return;

    event.preventDefault();

    const guide = document.getElementById(
      button.dataset.z2OpenSizeGuide
    );

    if (guide instanceof Z2SizeGuideModal) {
      guide.open(button);
    } else {
      console.warn(
        'ZONE2: voeg de maattabelsectie toe aan dit producttemplate.'
      );
    }
  });

  document.addEventListener('keydown', event => {
    activeGuide?.handleKeydown(event);
  });

  document.addEventListener('focusin', event => {
    if (
      activeGuide &&
      !activeGuide.dialog.contains(event.target)
    ) {
      activeGuide.focusStart();
    }
  });
})();